import React from 'react';
import {
    Avatar,
    Box,
    Chip,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import {
    getEntityTypeDesc,
    getEntityTypeLabel,
    getEntityTypeMeta,
} from '../../../utils/entityTypeMeta';

const CHIP_COLOR_MAP = {
    GPE: 'info',
    LOC: 'info',
    ORG: 'success',
    PERSON: 'secondary',
    PER: 'secondary',
    EVENT: 'warning',
    FAC: 'primary',
    NORP: 'default',
    PRODUCT: 'default',
    LAW: 'secondary',
    DATE: 'default',
    TIME: 'default',
    MONEY: 'default',
    QUANTITY: 'default',
};

function getEntityTypeChipColor(type) {
    return (type && CHIP_COLOR_MAP[type.toUpperCase()]) || 'default';
}

/**
 * Single entity row inside the Entities tab legend.
 *
 * Props:
 *   entity      — chart entity object (label, imageUrl, wikidataId, description)
 *   entityType  — NER type string (PERSON, ORG, GPE, …)
 *   onClick     — called when the row card is clicked (opens entity details)
 *   propertyTags — optional array of { label, token, propertyKey } to render
 *                  as clickable filter-shortcut chips below the entity name.
 *   onTagClick  — optional (token, propertyKey) => void; called when a tag chip is clicked.
 */
export default function WhiteboardChartEntityRow({entity, entityType, onClick, propertyTags, onTagClick}) {
    const imageUrl = entity.imageUrl || undefined;
    const typeLabel = getEntityTypeLabel(entityType) || entityType;

    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                px: 1.5,
                py: 1.25,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                textAlign: 'left',
                cursor: 'pointer',
                font: 'inherit',
                color: 'inherit',
                transition: 'background-color 0.15s',
                '&:hover': {
                    bgcolor: 'action.hover',
                },
            }}
        >
            {/* Avatar + name row */}
            <Stack direction="row" alignItems="flex-start" gap={1.5}>
                <Stack alignItems="center" spacing={0.75} sx={{flexShrink: 0, width: 56}}>
                    <Avatar
                        src={imageUrl}
                        alt={entity.label}
                        slotProps={{
                            img: {
                                referrerPolicy: 'strict-origin-when-cross-origin',
                            },
                        }}
                        sx={{
                            width: 48,
                            height: 48,
                            bgcolor: getEntityTypeMeta(entityType).color,
                        }}
                    >
                        {!imageUrl && String(entity.label || '?').slice(0, 1).toUpperCase()}
                    </Avatar>

                    {entityType ? (
                        <Tooltip title={getEntityTypeDesc(entityType) || entityType} arrow placement="left">
                            <Chip
                                label={typeLabel}
                                size="small"
                                color={getEntityTypeChipColor(entityType)}
                                variant="outlined"
                                sx={{
                                    height: 22,
                                    maxWidth: '100%',
                                    '& .MuiChip-label': {
                                        px: 0.75,
                                        fontSize: '0.68rem',
                                        whiteSpace: 'normal',
                                        lineHeight: 1.2,
                                    },
                                }}
                            />
                        </Tooltip>
                    ) : null}
                </Stack>

                <Stack sx={{flex: 1, minWidth: 0}} spacing={0.5}>
                    <Typography
                        fontWeight={600}
                        sx={{
                            lineHeight: 1.35,
                            wordBreak: 'break-word',
                        }}
                    >
                        {entity.label}
                    </Typography>

                    {entity.description ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                                lineHeight: 1.4,
                                wordBreak: 'break-word',
                                whiteSpace: 'normal',
                            }}
                        >
                            {entity.description}
                        </Typography>
                    ) : null}
                </Stack>
            </Stack>

            {/* Property tag quick-filter chips */}
            {propertyTags && propertyTags.length > 0 && (
                <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{mt: 0.75, pl: 8 /* align under entity name, past avatar */}}
                    onClick={(e) => e.stopPropagation() /* don't fire the card click */}
                >
                    {propertyTags.map((tag) => (
                        <Tooltip
                            key={`${tag.propertyKey}:${tag.token}`}
                            title={`Filter by "${tag.label}"`}
                            placement="top"
                            enterDelay={600}
                        >
                            <Chip
                                label={tag.label}
                                size="small"
                                variant="outlined"
                                color={tag.active ? 'primary' : 'default'}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onTagClick?.(tag.token, tag.propertyKey);
                                }}
                                sx={{
                                    fontSize: 10,
                                    height: 20,
                                    fontWeight: tag.active ? 700 : 400,
                                    cursor: 'pointer',
                                    '& .MuiChip-label': {px: 0.75},
                                }}
                            />
                        </Tooltip>
                    ))}
                </Stack>
            )}
        </Box>
    );
}
